import { useContext, useEffect, useState } from 'react'
import SlideOver from 'src/components/ui/SlideOver/SlideOver'
import { CloseButtonIcon } from 'src/components/Icons/CloseButtonIcon'
import { InfoIcon } from 'src/components/Icons/InfoIcon'
import { ShieldCheck } from 'src/components/Icons/ShieldCheck'
import { formatterPrice } from 'src/utils/formatterPrice'
import { ProductContext } from 'src/components/restructure/product/contexts'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { ChevronRight } from 'src/components/Icons/ChevronRight'
import { buildInsuranceCustomData } from 'src/components/common/SourceTracker/utils/buildCustomData'
import DktLoader from 'src/components/DktLoader/DktLoader'
import { publish } from 'src/components/restructure/product/productEvents'
import FlexibleLink from 'src/components/ui/FlexibleLink/FlexibleLink'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import type { Product } from 'src/components/restructure/product/types/product'
import { priceRangeCoversBike } from 'src/utils/insurance'
import { useScrollBlock } from 'src/utils/useScrollBlock'
import { useModal } from 'src/sdk/ui/modal/Provider'

import storeConfig from '../../../../../../../../../store.config'
import { useBuyButton } from '../hooks/useBuyButton'
import { fetchProductInsurance } from '../hooks/usefetchProductInsurance'

interface InsuranceBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  onOpenLearnMore: () => void
  onSuccess: () => void
}

export function InsuranceBottomSheet({
  isOpen,
  onClose,
  onOpenLearnMore,
  onSuccess,
}: InsuranceBottomSheetProps) {
  const [insuranceProduct, setInsuranceProduct] = useState<Product[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const { addToCart } = useBuyButton()
  const { setIdsToShow } = useCartFeedback()
  const { allowScroll, blockScroll } = useScrollBlock()
  const { onModalClose } = useModal()
  const {
    addCustomData,
    orderForm: { customData, items },
    isLoading,
  } = useCheckout()

  const { currentSku } = useContext(ProductContext)
  const skuBikePrice = currentSku?.sellerDefault?.sellerOffer?.price

  useEffect(() => {
    if (!isOpen) {
      return
    }

    async function fetchData() {
      try {
        const data = await fetchProductInsurance({
          productIds: storeConfig.InsuranceID,
        })

        setInsuranceProduct(data)
        // Seleciona o primeiro item válido por padrão
        const firstValid = data?.[0]?.items?.find((item) =>
          priceRangeCoversBike(item.referenceId, skuBikePrice)
        )

        setSelected(firstValid?.itemId ?? null)
      } catch (error) {
        console.error('Erro ao buscar Seguros')
      }
    }

    fetchData()
  }, [isOpen, skuBikePrice])

  // Verifica se já existe seguro no carrinho que cobre o valor da bike atual
  useEffect(() => {
    if (!isOpen || !skuBikePrice) {
      return
    }

    // Verifica se já existe algum seguro no carrinho que cobre o valor da bike
    const hasInsuranceInCart = items?.some((item) => {
      // Busca o item nos dados de seguro
      const insuranceProductItems = insuranceProduct?.flatMap(
        (product) => product?.items ?? []
      )

      const matchingInsurance = insuranceProductItems.find(
        (ins) => ins.itemId === item.id
      )

      if (!matchingInsurance) {
        return false
      }

      // Verifica se esse seguro cobre o valor da bike atual
      return priceRangeCoversBike(matchingInsurance.referenceId, skuBikePrice)
    })

    // Se já houver seguro no carrinho que cobre o valor, fecha o modal
    if (hasInsuranceInCart) {
      onClose()
    }
  }, [isOpen, items, insuranceProduct, skuBikePrice, onClose])

  // Filtra apenas os seguros que cobrem o valor da bike
  const insuranceItemsOnPriceRange = insuranceProduct
    ?.flatMap((product) => product?.items ?? [])
    ?.filter((insurance) =>
      priceRangeCoversBike(insurance?.referenceId, skuBikePrice)
    )

  const handleAddInsurance = async () => {
    document.documentElement.classList.remove('no-scroll')
    publish('addToCart')

    const insurance = insuranceItemsOnPriceRange.find(
      (p) => p.itemId === selected
    )

    if (!insurance) {
      return
    }

    // Merge dos seguros antigos com o novo
    const customApps = customData?.customApps ?? []

    const currentInsurance = customApps?.find((app) => app.id === 'insurance')
      ?.fields?.data as string

    const data = [{ [currentSku.itemId]: insurance.itemId }]

    if (currentInsurance) {
      const currentInsuranceData: Array<Record<string, string>> = JSON.parse(
        currentInsurance ?? ''
      )

      currentInsuranceData?.forEach((insuranceData) => data.push(insuranceData))
    }

    // Envia para o customData usando o helper
    await addCustomData(buildInsuranceCustomData(JSON.stringify(data)), true)

    // Verifica se já existe o seguro no carrinho para pegar a quantidade atual
    const existingInsuranceInCart = items?.find(
      (item) => item.id === insurance.itemId
    )

    const currentQuantity = existingInsuranceInCart?.quantity ?? 0
    const newQuantity = currentQuantity + 1

    // Adiciona o seguro ao carrinho com a quantidade atualizada
    await addToCart({
      item: {
        id: insurance.itemId,
        productId: insurance.productId,
        seller: insurance.sellerDefault.sellerId,
        quantity: newQuantity,
        index: 0,
        price: insurance.offer?.price,
      },
      type: 'addToCart',
      shouldOpenFeedback: false,
    })

    // Sempre mostra modal de sucesso após adicionar seguro

    setIdsToShow([currentSku.itemId, insurance.itemId])
    onSuccess()
  }

  // Restaura o estado global do modal (classe `no-scroll` em <html> e `fade`)
  // quando o sheet fecha por qualquer caminho ou desmonta (ex.: navegacao).
  useEffect(() => {
    if (!isOpen) {
      return
    }

    return () => onModalClose()
  }, [isOpen, onModalClose])

  useEffect(() => {
    isOpen ? blockScroll() : allowScroll()

    return () => allowScroll()
  }, [allowScroll, blockScroll, isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <SlideOver
      isOpen={isOpen}
      onDismiss={onClose}
      size="partial"
      className="w-full sm:w-[360px] bg-white"
      direction="rightSide"
    >
      <div
        data-testid="pdp-bike-insurance-sheet"
        className="flex flex-col h-full min-h-screen p-4 overflow-y-auto"
      >
        <header className="flex justify-end items-start">
          <h2 className="text-xl font-medium mb-6">
            Aproveite e adicione o seguro para sua bike
          </h2>
          <button
            data-testid="pdp-bike-insurance-close"
            className="cursor-pointer"
            onClick={onClose}
          >
            <CloseButtonIcon size={24} />
          </button>
        </header>
        <div className="flex-1 flex flex-col">
          <div className="mb-4 w-full">
            {insuranceItemsOnPriceRange.length === 0 && (
              <div className="text-center text-[#616161]">
                Não há seguros disponíveis para o valor da sua bike.
              </div>
            )}
            {insuranceItemsOnPriceRange.map((product) => (
              <button
                key={product.itemId}
                className={`block w-full items-center p-4 border-[2px] cursor-pointer text-left ${
                  selected === product.itemId
                    ? ' border-[#2F4ACB] bg-[#F5F6FB]'
                    : ' border-[#E5E5E5] bg-[#F8F8F8]'
                }`}
                onClick={() => setSelected(product.itemId)}
                type="button"
                style={{ marginBottom: '16px' }}
              >
                <div className="flex items-center">
                  <span className="mr-3">
                    <ShieldCheck />
                  </span>
                  <div className="flex-1 text-start">
                    <h1 className="font-bold text-lg mt-0">{product.name}</h1>
                    <div className="text-sm text-[#101010] font-normal">
                      {product.offer?.price
                        ? `${formatterPrice(product.offer.price)} à vista`
                        : ''}
                    </div>
                    <p className="text-[10px] text-[#101010] font-normal">
                      Consulte as opções de <br /> parcelamento no checkout
                    </p>
                  </div>
                  <input
                    type="radio"
                    checked={selected === product.itemId}
                    onChange={() => setSelected(product.itemId)}
                    className="ml-3 w-5 h-5 accent-[#2F4ACB]"
                  />
                </div>
              </button>
            ))}
          </div>
          <button
            className="w-full flex items-center justify-between px-6 py-4 bg-[#ececf7] rounded mb-6"
            onClick={onOpenLearnMore}
            type="button"
          >
            <span className="flex items-center gap-2">
              <InfoIcon />
              <span className="font-semibold text-[#2F4ACB] text-base">
                Saiba como funciona
              </span>
            </span>
            <ChevronRight className="text-[#2F4ACB]" />
          </button>
        </div>
        <footer className="mt-auto">
          <p className="text-sm text-[#101010] font-normal mb-4">
            Ao adicionar, declaro que li e concordo com os{' '}
            <FlexibleLink
              linkUrl="https://decathlonstore.vteximg.com.br/arquivos/Termos_Condições_Seguro.pdf"
              classNames="underline"
              label="termos e condições"
            />{' '}
            de contratação do seguro selecionado.
          </p>
          <button
            className="w-full h-12 rounded-full font-bold text-white text-lg mb-4 bg-[#2F4ACB]"
            onClick={handleAddInsurance}
            type="button"
            disabled={insuranceItemsOnPriceRange.length === 0}
          >
            {isLoading ? (
              <div className="flex items-center justify-center w-full">
                <DktLoader width={20} variant="white" />
              </div>
            ) : (
              'Adicionar Seguro'
            )}
          </button>
          <button
            data-testid="pdp-bike-insurance-decline"
            className="w-full h-12 rounded-full border border-[#B3BAC3] text-[#101010] text-lg font-bold bg-white"
            onClick={onClose}
            type="button"
          >
            Não quero
          </button>
        </footer>
      </div>
    </SlideOver>
  )
}
